import * as React from 'react'
import { CalendarDays, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatDateInput } from '@/lib/dates'

function parseLocalDateTime(value: string) {
  if (!value) return undefined
  const [date] = value.split('T')
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function dateValue(date: Date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

const displayDate = formatDateInput

function displayTime(value: string) {
  if (!value) return ''

  const [hour, minute] = value.split(':').map(Number)
  const suffix = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour % 12 || 12
  return `${displayHour}:${String(minute).padStart(2, '0')} ${suffix}`
}

function parseTime(value: string) {
  const match = value.trim().match(/^(1[0-2]|0?[1-9]):([0-5][0-9])\s*(AM|PM)$/i)
  if (!match) return undefined

  const hour = (Number(match[1]) % 12) + (match[3].toUpperCase() === 'PM' ? 12 : 0)
  return `${String(hour).padStart(2, '0')}:${match[2]}`
}

export function DatePicker({
  id,
  label,
  value,
  onChange,
  placeholder,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
}) {
  const selectedDate = value ? parseLocalDateTime(`${value}T00:00`) : undefined

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            className="h-9 min-w-40 justify-start px-3 font-normal"
            aria-label={selectedDate ? label : `Choose ${label.toLowerCase()}`}
          >
            <CalendarDays className="mr-2 h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            {displayDate(selectedDate) ?? placeholder ?? `Choose ${label.toLowerCase()}`}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            mode="single"
            selected={selectedDate}
            defaultMonth={selectedDate}
            onSelect={(date) => date && onChange(dateValue(date))}
          />
        </PopoverContent>
      </Popover>
      {selectedDate && (
        <Button
          type="button"
          variant="outline"
          className="h-9 w-9 border-transparent p-0 text-muted-foreground"
          aria-label={`Clear ${label.toLowerCase()}`}
          onClick={() => onChange('')}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      )}
    </div>
  )
}

export function DateTimePicker({
  id,
  label,
  value,
  onChange,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
}) {
  const selectedDate = parseLocalDateTime(value)
  const time = value.split('T')[1] ?? ''
  const dateLabel = label.replace(' and time', '')
  const [inputValue, setInputValue] = React.useState(displayTime(time))

  React.useEffect(() => setInputValue(displayTime(time)), [time])

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="h-9 min-w-48 justify-start px-3 font-normal"
            aria-label={selectedDate ? dateLabel : `Choose ${dateLabel.toLowerCase()}`}
          >
            <CalendarDays className="mr-2 h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            {displayDate(selectedDate) ?? `Choose ${dateLabel.toLowerCase()}`}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            mode="single"
            selected={selectedDate}
            defaultMonth={selectedDate}
            onSelect={(date) => date && onChange(`${dateValue(date)}T${time || '00:00'}`)}
          />
        </PopoverContent>
      </Popover>
      <Input
        id={id}
        aria-label={label.replace('date and ', '')}
        type="text"
        className="h-9 w-28"
        disabled={!selectedDate}
        value={inputValue}
        placeholder="6:00 PM"
        onChange={(event) => setInputValue(event.target.value)}
        onBlur={(event) => {
          const parsedTime = parseTime(event.target.value)
          if (selectedDate && parsedTime) onChange(`${dateValue(selectedDate)}T${parsedTime}`)
          else setInputValue(displayTime(time))
        }}
      />
      {selectedDate && (
        <Button
          type="button"
          variant="outline"
          className="h-9 w-9 border-transparent p-0 text-muted-foreground"
          aria-label={`Clear ${label.toLowerCase()}`}
          onClick={() => onChange('')}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      )}
    </div>
  )
}

export function TimePicker({
  id,
  label,
  value,
  disabled = false,
  onChange,
  onClear,
}: {
  id: string
  label: string
  value: string
  disabled?: boolean
  onChange: (value: string) => void
  onClear?: () => void
}) {
  const [inputValue, setInputValue] = React.useState(displayTime(value))

  React.useEffect(() => setInputValue(displayTime(value)), [value])

  return (
    <div className="flex items-center gap-2">
      <Input
        id={id}
        aria-label={label}
        type="text"
        className="h-9 w-full"
        disabled={disabled}
        value={inputValue}
        placeholder="6:00 PM"
        onChange={(event) => setInputValue(event.target.value)}
        onBlur={(event) => {
          const parsedTime = parseTime(event.target.value)
          if (parsedTime) onChange(parsedTime)
          else setInputValue(displayTime(value))
        }}
      />
      {onClear && value && (
        <Button
          type="button"
          variant="outline"
          className="h-9 w-9 shrink-0 border-transparent p-0 text-muted-foreground"
          aria-label={`Clear ${label.toLowerCase()}`}
          onClick={onClear}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      )}
    </div>
  )
}
